import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import styles from '@components/CareProfile.module.css';
import BaseErrors from '@components/fields/BaseErrors';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import {
  CareProfileFragment as CareProfile,
  CareProfileEmotionalConcernEnum,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForEmotionalConcern } from '@utils/optionUtils';

type FormData = {
  emotionalConcerns: CareProfileEmotionalConcernEnum[];
  emotionalConcernsOther: string | null;
  id: string;
};

type Props = {
  careProfile: CareProfile;
  consultId: string | undefined | null;
  initialConsultReportId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultSocialForm = ({
  careProfile,
  consultId,
  initialConsultReportId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('well-being');
      } else {
        onSuccess('financial');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const initialValues = {
    emotionalConcerns: careProfile?.emotionalConcerns?.emotionalConcernsOptions ?? [],
    emotionalConcernsOther: careProfile?.emotionalConcerns?.other ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);

  const schema = yup
    .object()
    .shape({
      emotionalConcerns: yup.array(),
      emotionalConcernsOther: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const emotionalConcerns = form.watch('emotionalConcerns');

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        emotionalConcerns: {
          emotionalConcernsOptions: form.getValues().emotionalConcerns,
          other: form.getValues().emotionalConcernsOther
        },
        id: initialConsultReportId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (questions) {
            onSuccess();
          } else {
            setSubmitted(true);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [consultId, form, initialConsultReportId, intl, onSuccess, questions, updateInitialConsultReport]);

  const emotionalConcernOptions = useMemo(() => optionsForEmotionalConcern(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <CheckboxGroup
        control={form.control}
        errors={form.formState.errors.emotionalConcerns}
        label={intl.formatMessage({ id: 'models.initialConsultReport.questions.emotional_concerns.content' })}
        name="emotionalConcerns"
        options={emotionalConcernOptions}
      />
      <div className={emotionalConcerns.includes(CareProfileEmotionalConcernEnum.other) ? undefined : 'ion-hide'}>
        <Input
          control={form.control}
          error={form.formState.errors.emotionalConcernsOther}
          label={intl.formatMessage({ id: 'dictionary.other' })}
          name="emotionalConcernsOther"
          spellcheck
        />
      </div>

      {!questions ? (
        <div className={styles.spaceBetween}>
          <SecondaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(false)}
            textKey="dictionary.previous"
            type="submit"
          />
          <PrimaryButton
            data-test="social-next"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(true)}
            textKey="dictionary.next"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default InitialConsultSocialForm;
